import React, { useEffect, useState } from "react";

import Head from "next/head";
import { useRouter } from "next/router";

import { FooterData } from "data/footer";
import { StorefrontData, StoreFrontModals, QuestionnaireData } from "data/storefront";

import {
  CREATOR_STUDIO_PAGE_LOGIN,
  CREATOR_STUDIO_PAGE_VISIT,
} from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { useUserPurchases } from "~/hooks/purchase/useUserPurchases";
import { getPathForProduct } from "~/util/stringUtils";

import { LandingNavbarWithCTA } from "../common/LandingNavbarWithCTA/LandingNavbarWithCTA";
import { MarketingFooter } from "../common/MarketingFooter";
import { OverlayModal } from "../common/OverlayModal";
import { LandingPageSources } from "../homepage/useLandingPage";
import { VideoPlayer } from "../videoProductPage/common/VideoPlayer/VideoPlayer";
import { EmptySkeleton } from "./EmptySkeleton";
import { InfiniteVideoCards } from "./InfiniteVideoCards";
import { DescModal } from "./Modals/DescModal";
import { FollowForm } from "./Modals/FollowForm";
import { ShareModal } from "./Modals/ShareModal";
import { Questionnaire } from "./Questionnaire";
import { ScrollingTabs } from "./ScrollingTabs";
import * as styles from "./Storefront.css";
import { StorefrontHeroSection } from "./StorefrontHeroSection";
import { sortProductsByIndex } from "./utils/videoCardUtils";

interface Props {
  storefrontData: StorefrontData;
  footerData: FooterData;
}

const StorefrontHead = ({
  storefrontData,
}: {
  storefrontData: StorefrontData;
}): React.ReactElement => {
  const description =
    storefrontData.creatorName +
    "'s Studio:" +
    " hands-on classes by " +
    storefrontData.creatorName;

  let creatorWithApostrophe = storefrontData.creatorName.trim();

  if (storefrontData.creatorName.endsWith("s")) {
    creatorWithApostrophe += "'";
  } else {
    creatorWithApostrophe += "'s";
  }
  return (
    <Head>
      <title>{description}</title>
      <meta
        property="og:title"
        content={creatorWithApostrophe + " Studio: " + storefrontData.title}
      />
      <meta name="description" content={description} />
      <meta name="og:description" content={description} />
      <meta property="og:image" content={storefrontData.welcomePosterImageURL} />
      <link rel="canonical" href={"https://studio.com"} />
    </Head>
  );
};

const StorefrontComponent = ({
  storefrontData,
  setShowModal,
  isUserFollowingStorefront,
}: {
  storefrontData: StorefrontData;
  setShowModal: (modal: StoreFrontModals) => void;
  isUserFollowingStorefront: boolean;
}): React.ReactElement => {
  const [filter, setFilter] = useState("All Classes");
  const { userPurchases } = useUserPurchases();

  const tagsToUse = ["All Classes", ...storefrontData.tags];
  console.log("StorefrontData: ", storefrontData);
  return (
    <>
      <StorefrontHeroSection
        isUserFollowingStorefront={isUserFollowingStorefront}
        storefrontData={storefrontData}
        setShowModal={setShowModal}
      />
      <div className={styles.container}>
        {storefrontData.products.length > 0 ? (
          <>
            <ScrollingTabs tabs={tagsToUse} setFilter={(tag: string) => setFilter(tag)} />

            <InfiniteVideoCards
              products={storefrontData.products.sort(sortProductsByIndex)}
              productPurchases={userPurchases}
              filter={filter}
            />
          </>
        ) : (
          <>
            <EmptySkeleton />
          </>
        )}
      </div>
    </>
  );
};

export const Storefront = ({ storefrontData, footerData }: Props): React.ReactElement => {
  const { trackAmplitudeEvent } = useAnalytics();

  const [showModal, setShowModal] = useState<StoreFrontModals>();
  const [isUserFollowing, setIsUserFollowing] = useState(false);
  const router = useRouter();

  useEffect(() => {
    trackAmplitudeEvent(CREATOR_STUDIO_PAGE_VISIT, {
      ...addUTMPropertiesToEvent(),
      creatorName: storefrontData.creatorName,
      storefrontSlug: storefrontData.slug,
      productCount: storefrontData.products.length,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <OverlayModal
        isOpen={showModal != null}
        contentClass={
          showModal === "desc"
            ? styles.overlayModalBlack
            : showModal === "intro"
            ? styles.overlayModalIntro
            : showModal === "share"
            ? styles.overlayModalShare
            : styles.overlayModal
        }
        onClose={() => {
          setShowModal(null);
        }}
      >
        <>
          {showModal === "follow" && (
            <FollowForm setIsUserFollowing={setIsUserFollowing} storefrontData={storefrontData} />
          )}
          {showModal === "share" && <ShareModal storefrontData={storefrontData} />}
          {showModal === "intro" && (
            <>
              <div>
                <VideoPlayer
                  url={storefrontData.welcomeVideoURL}
                  light={storefrontData.welcomePosterImageURL}
                  height={"500px"}
                />
              </div>
            </>
          )}
          {showModal === "desc" && <DescModal storefrontData={storefrontData} />}
        </>
      </OverlayModal>
      <StorefrontHead storefrontData={storefrontData} />
      <LandingNavbarWithCTA
        onSignInEvent={() => {
          trackAmplitudeEvent(CREATOR_STUDIO_PAGE_LOGIN, addUTMPropertiesToEvent());
        }}
        source={LandingPageSources.Storefront}
      />
      <div className={styles.outerContainer}>
        <StorefrontComponent
          {...{ storefrontData, setShowModal }}
          isUserFollowingStorefront={isUserFollowing}
        />
        <MarketingFooter data={footerData} />
      </div>
    </>
  );
};
